// ============================================================
// RecipientPortal — the client-facing secure workspace (End User Portal).
//
// A recipient opens a signed secure link and lands here. The portal restores a
// verified session from server truth, or walks the honest journey: preparing →
// ready → redeem (signed link, with a hidden fallback-code path) → persisted
// NDA + notification preferences → the REAL project workspace; or a fail-closed
// state (expired/revoked/denied/provider-unavailable/…).
//
// Hard rules honored here:
//   - Branding (owner decision): the visible product is SQUARE CODE or the
//     configured client brand. Internal/trust-layer identifiers NEVER render
//     (see the recipient branding-scan test).
//   - The happy path redeems the signed link; the code fallback stays hidden
//     until the workspace asks for it or the recipient chooses it. The code is
//     sent in the request body only.
//   - Everything shown after verification comes from the server manifest —
//     published modules only, honest capability states, no fabricated content.
//   - Status is conveyed by icon + label + explanation + action — never color alone.
//   - Responsive (mobile-first), theme-aware, keyboard-accessible, reduced-motion.
//
// The stylesheet is the scoped `RECIPIENT_CSS` string below, injected inline so
// the SSR/node test env renders it without a CSS loader.
// ============================================================

import { useSecureInvitation, type SecureInvitationState, type ModuleUpdate } from './useSecureInvitation'
import {
  RECIPIENT_COPY, FALLBACK_VIEWS, RETRYABLE_VIEWS,
  type RecipientView, type SecureInvitationDisplay,
} from './secureInvitation'
import type { RecipientManifest } from '../../../api/client'

const ONBOARDING_VIEWS: RecipientView[] = ['nda', 'notification_preferences']

function formatExpiry(iso: string | null): string | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  try {
    return new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

// ── Pure presentational view (exported for SSR/unit tests of every state) ──

export interface RecipientStateViewProps {
  view: RecipientView
  display: SecureInvitationDisplay
  manifest: RecipientManifest | null
  updates: ModuleUpdate[] | null
  message: string
  busy: boolean
  showFallback: boolean
  fallbackCode: string
  fallbackNotice: string | null
  emailUpdates: boolean
  onEmailUpdates: (v: boolean) => void
  onContinue: () => void
  onRetry: () => void
  onRevealFallback: () => void
  onHideFallback: () => void
  onFallbackCode: (v: string) => void
  onSubmitFallback: () => void
}

export function RecipientStateView(props: RecipientStateViewProps) {
  const { view, display, manifest, message } = props
  const copy = RECIPIENT_COPY[view]
  const expiry = formatExpiry(display.expiresAt)
  const isOnboarding = ONBOARDING_VIEWS.includes(view)
  const canRetry = RETRYABLE_VIEWS.includes(view)
  const canFallback = FALLBACK_VIEWS.includes(view)
  const showCodeForm = view === 'code_required' || (canFallback && props.showFallback)
  const brand = manifest?.branding.client_brand || manifest?.branding.product_name || 'Square Code'
  const workspaceName = manifest?.workspace.name ?? display.projectName

  return (
    <main className="sia" data-tone={copy.tone} data-view={view}>
      <style dangerouslySetInnerHTML={{ __html: RECIPIENT_CSS }} />
      <div className="sia-card" role="group" aria-labelledby="sia-title" data-wide={view === 'portal_home' || undefined}>
        <header className="sia-head">
          <span className="sia-badge" data-tone={copy.tone}>
            <span className="sia-badge-icon" aria-hidden="true">{copy.icon}</span>
            <span className="sia-badge-label">{copy.label}</span>
          </span>
          {workspaceName ? <p className="sia-project">{workspaceName}</p> : null}
        </header>

        {/* aria-live so state transitions are announced to screen readers */}
        <section className="sia-status" role="status" aria-live="polite">
          <h1 id="sia-title" className="sia-title">
            {view === 'portal_home' && workspaceName ? workspaceName : copy.title}
          </h1>
          <p className="sia-body">{message || copy.body}</p>

          {(view === 'preparing' || view === 'redeeming') ? (
            <div className="sia-spinner" aria-hidden="true"><span /><span /><span /></div>
          ) : null}

          {display.inviterName && (view === 'ready' || view === 'expires_soon') ? (
            <p className="sia-meta">Invited by {display.inviterName}
              {display.purpose ? <> · {display.purpose}</> : null}
            </p>
          ) : null}

          {expiry && (view === 'ready' || view === 'expires_soon') ? (
            <p className="sia-meta" data-tone={view === 'expires_soon' ? 'attention' : 'neutral'}>
              Access expires {expiry}
            </p>
          ) : null}
        </section>

        {view === 'nda' && manifest ? (
          <div className="sia-nda" role="note">
            <p>
              By continuing you agree to keep the contents of this workspace
              confidential and to use them only for the purpose you were invited
              for (agreement version {manifest.onboarding.nda.version}). Your
              acceptance is recorded with a timestamp.
            </p>
          </div>
        ) : null}

        {view === 'notification_preferences' ? (
          <ul className="sia-channels" aria-label="Notification channels">
            <li>
              <span className="sia-ch-on" aria-hidden="true">●</span> In your workspace
              <em>Always on</em>
            </li>
            <li>
              <label className="sia-ch-toggle">
                <input
                  type="checkbox"
                  checked={props.emailUpdates}
                  onChange={(e) => props.onEmailUpdates(e.target.checked)}
                />
                <span>Email updates</span>
                <em>Saved as a preference — delivery depends on your host’s email setup</em>
              </label>
            </li>
          </ul>
        ) : null}

        {view === 'portal_home' && manifest ? (
          <PortalHome manifest={manifest} updates={props.updates} />
        ) : null}

        <div className="sia-actions">
          {(view === 'ready' || view === 'expires_soon') ? (
            <button type="button" className="sia-btn sia-btn-primary" onClick={props.onContinue} disabled={props.busy}>
              Continue
            </button>
          ) : null}

          {view === 'nda' ? (
            <button type="button" className="sia-btn sia-btn-primary" onClick={props.onContinue} disabled={props.busy}>
              Accept and continue
            </button>
          ) : null}

          {view === 'notification_preferences' ? (
            <button type="button" className="sia-btn sia-btn-primary" onClick={props.onContinue} disabled={props.busy}>
              Save and continue
            </button>
          ) : null}

          {canRetry ? (
            <button type="button" className="sia-btn sia-btn-primary" onClick={props.onRetry} disabled={props.busy}>
              Try again
            </button>
          ) : null}

          {canFallback && !props.showFallback ? (
            <button type="button" className="sia-btn sia-btn-ghost" onClick={props.onRevealFallback}>
              Use an access code
            </button>
          ) : null}
        </div>

        {/* One-time access-code entry — the hidden fallback redemption path.
            The code travels in the request body only; denials stay generic. */}
        {showCodeForm ? (
          <form
            className="sia-fallback"
            onSubmit={(e) => { e.preventDefault(); props.onSubmitFallback() }}
          >
            <label htmlFor="sia-code" className="sia-label">Access code</label>
            <input
              id="sia-code"
              className="sia-input"
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={props.fallbackCode}
              onChange={(e) => props.onFallbackCode(e.target.value)}
              placeholder="Enter the code you were given"
            />
            {props.fallbackNotice ? (
              <p className="sia-notice" role="status" aria-live="polite">{props.fallbackNotice}</p>
            ) : null}
            <div className="sia-actions">
              <button type="submit" className="sia-btn sia-btn-primary" disabled={props.busy}>Continue</button>
              {view !== 'code_required' ? (
                <button type="button" className="sia-btn sia-btn-ghost" onClick={props.onHideFallback}>Cancel</button>
              ) : null}
            </div>
          </form>
        ) : null}

        <footer className="sia-foot">
          <span className="sia-lock" aria-hidden="true">🔒</span>
          <span>{`Protected by ${brand} Secure Access`}</span>
        </footer>
      </div>
    </main>
  )
}

// ── The real recipient workspace: manifest-driven, published modules only. ──

function PortalHome({ manifest, updates }: { manifest: RecipientManifest | null; updates: ModuleUpdate[] | null }) {
  if (!manifest) return null
  const expiry = formatExpiry(manifest.session.expires_at)
  return (
    <div className="sia-home">
      {manifest.preview ? (
        <p className="sia-preview-flag" role="note">
          Preview — this is what your recipient will see.
        </p>
      ) : null}

      {manifest.workspace.summary ? (
        <p className="sia-summary">{manifest.workspace.summary}</p>
      ) : null}

      {manifest.modules.length ? (
        <ul className="sia-modules" aria-label="Workspace sections">
          {manifest.modules.map((m) => (
            <li key={m.key} data-state={m.state}>
              <span className="sia-module-label">{m.label}</span>
              <em>{m.state === 'live' ? 'Available' : 'Coming soon'}</em>
            </li>
          ))}
        </ul>
      ) : (
        <p className="sia-meta">Nothing has been shared with you in this workspace yet.</p>
      )}

      {updates && updates.length ? (
        <section className="sia-updates" aria-label="Updates">
          <h2>Updates</h2>
          <ul>
            {updates.map((u) => (
              <li key={u.id}>
                <p>{u.body}</p>
                <time dateTime={u.published_at}>{formatExpiry(u.published_at)}</time>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {expiry ? (
        <p className="sia-meta">Your secure session ends {expiry}. Reopen your link to continue after that.</p>
      ) : null}
    </div>
  )
}

// ── Container: wires the journey hook to the presentational view ──

export default function RecipientPortal() {
  const s: SecureInvitationState = useSecureInvitation()
  return (
    <RecipientStateView
      view={s.view}
      display={s.display}
      manifest={s.manifest}
      updates={s.updates}
      message={s.message}
      busy={s.busy}
      showFallback={s.showFallback}
      fallbackCode={s.fallbackCode}
      fallbackNotice={s.fallbackNotice}
      emailUpdates={s.emailUpdates}
      onEmailUpdates={s.setEmailUpdates}
      onContinue={s.continueOnboarding}
      onRetry={s.retry}
      onRevealFallback={s.revealFallback}
      onHideFallback={s.hideFallback}
      onFallbackCode={s.setFallbackCode}
      onSubmitFallback={s.submitFallback}
    />
  )
}

// ── Recipient surface stylesheet (scoped under `.sia`) ──
// Calm, trustworthy, theme-aware. Tokens on `.sia`; dark via prefers-color-scheme
// and the shell's data-theme override. Status color is a SUPPORT to icon+label,
// never the sole signal.
export const RECIPIENT_CSS = `
.sia{
  --sia-bg:#eef1f8; --sia-panel:#ffffff; --sia-ink:#161a26; --sia-muted:#5b6478;
  --sia-line:#e2e6f0; --sia-accent:#4f5bd5; --sia-accent-ink:#ffffff;
  --sia-ready:#1f8a5b; --sia-attention:#b7791f; --sia-blocked:#c0392b; --sia-neutral:#5b6478;
  --sia-focus:#4f5bd5;
  min-height:100vh; margin:0; box-sizing:border-box;
  display:flex; align-items:center; justify-content:center; padding:24px;
  background:
    radial-gradient(1100px 520px at 50% -10%, rgba(79,91,213,.10), transparent 60%),
    var(--sia-bg);
  color:var(--sia-ink);
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.sia *{box-sizing:border-box}
.sia-card{
  width:100%; max-width:34rem; background:var(--sia-panel);
  border:1px solid var(--sia-line); border-radius:18px;
  padding:clamp(20px,4vw,36px); box-shadow:0 12px 40px -24px rgba(20,26,38,.5);
  display:flex; flex-direction:column; gap:18px;
}
.sia-card[data-wide]{max-width:46rem}
.sia-head{display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap}
.sia-badge{
  display:inline-flex; align-items:center; gap:8px; padding:5px 11px; border-radius:999px;
  font-size:.78rem; font-weight:650; letter-spacing:.01em;
  background:color-mix(in srgb, var(--sia-neutral) 12%, transparent); color:var(--sia-neutral);
}
.sia-badge[data-tone="ready"]{background:color-mix(in srgb,var(--sia-ready) 14%,transparent); color:var(--sia-ready)}
.sia-badge[data-tone="attention"]{background:color-mix(in srgb,var(--sia-attention) 16%,transparent); color:var(--sia-attention)}
.sia-badge[data-tone="blocked"]{background:color-mix(in srgb,var(--sia-blocked) 14%,transparent); color:var(--sia-blocked)}
.sia-badge[data-tone="progress"]{background:color-mix(in srgb,var(--sia-accent) 14%,transparent); color:var(--sia-accent)}
.sia-badge-icon{font-size:.95rem; line-height:1}
.sia-project{margin:0; font-size:.82rem; font-weight:600; color:var(--sia-muted); text-align:right}
.sia-status{display:flex; flex-direction:column; gap:10px}
.sia-title{margin:0; font-size:clamp(1.25rem,3.4vw,1.6rem); line-height:1.2; text-wrap:balance; letter-spacing:-.01em}
.sia-body{margin:0; font-size:1rem; line-height:1.55; color:var(--sia-muted); max-width:46ch}
.sia-meta{margin:0; font-size:.86rem; color:var(--sia-muted)}
.sia-meta[data-tone="attention"]{color:var(--sia-attention); font-weight:600}
.sia-nda{
  padding:14px 16px; border-radius:12px;
  background:color-mix(in srgb,var(--sia-neutral) 6%,transparent);
  border:1px solid var(--sia-line);
  font-size:.9rem; line-height:1.55; color:var(--sia-ink);
}
.sia-nda p{margin:0}
.sia-channels{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px}
.sia-channels li{
  display:flex; align-items:center; gap:10px; font-size:.92rem;
  padding:10px 12px; border:1px solid var(--sia-line); border-radius:10px;
}
.sia-channels li em{margin-left:auto; font-style:normal; font-size:.78rem; font-weight:600; color:var(--sia-muted); text-align:right; max-width:18ch}
.sia-ch-on{color:var(--sia-ready)}
.sia-ch-toggle{display:flex; align-items:center; gap:10px; width:100%; cursor:pointer}
.sia-ch-toggle input{width:18px; height:18px; accent-color:var(--sia-accent)}
.sia-ch-toggle input:focus-visible{outline:3px solid color-mix(in srgb,var(--sia-focus) 55%,transparent); outline-offset:2px}
.sia-ch-toggle em{margin-left:auto; font-style:normal; font-size:.78rem; font-weight:600; color:var(--sia-muted); text-align:right; max-width:22ch}
.sia-home{display:flex; flex-direction:column; gap:14px}
.sia-preview-flag{
  margin:0; padding:8px 12px; border-radius:10px; font-size:.82rem; font-weight:650;
  background:color-mix(in srgb,var(--sia-attention) 12%,transparent); color:var(--sia-attention);
}
.sia-summary{margin:0; font-size:.95rem; line-height:1.5; color:var(--sia-muted)}
.sia-modules{list-style:none; margin:0; padding:0; display:grid; gap:10px; grid-template-columns:repeat(auto-fill,minmax(13rem,1fr))}
.sia-modules li{
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding:12px 14px; border:1px solid var(--sia-line); border-radius:12px;
}
.sia-modules li[data-state="coming_soon"]{color:var(--sia-muted); background:color-mix(in srgb,var(--sia-neutral) 5%,transparent)}
.sia-module-label{font-weight:640; font-size:.92rem}
.sia-modules em{font-style:normal; font-size:.75rem; font-weight:650; color:var(--sia-muted)}
.sia-modules li[data-state="live"] em{color:var(--sia-ready)}
.sia-updates h2{margin:0 0 8px; font-size:1rem}
.sia-updates ul{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:10px}
.sia-updates li{padding:12px 14px; border:1px solid var(--sia-line); border-radius:12px}
.sia-updates p{margin:0 0 4px; font-size:.92rem; line-height:1.5}
.sia-updates time{font-size:.78rem; color:var(--sia-muted)}
.sia-actions{display:flex; flex-wrap:wrap; gap:10px}
.sia-btn{
  appearance:none; border:1px solid transparent; border-radius:10px; padding:11px 18px;
  font-size:.95rem; font-weight:640; cursor:pointer; min-height:44px;
  transition:transform .06s ease, background .12s ease, border-color .12s ease;
}
.sia-btn:focus-visible{outline:3px solid color-mix(in srgb,var(--sia-focus) 55%,transparent); outline-offset:2px}
.sia-btn:active{transform:translateY(1px)}
.sia-btn[disabled]{opacity:.55; cursor:not-allowed}
.sia-btn-primary{background:var(--sia-accent); color:var(--sia-accent-ink)}
.sia-btn-primary:hover{background:color-mix(in srgb,var(--sia-accent) 88%, #000)}
.sia-btn-ghost{background:transparent; color:var(--sia-accent); border-color:color-mix(in srgb,var(--sia-accent) 40%,transparent)}
.sia-btn-ghost:hover{background:color-mix(in srgb,var(--sia-accent) 8%,transparent)}
.sia-fallback{display:flex; flex-direction:column; gap:10px; padding-top:4px; border-top:1px solid var(--sia-line)}
.sia-label{font-size:.82rem; font-weight:640; color:var(--sia-ink)}
.sia-input{
  width:100%; padding:12px 14px; border:1px solid var(--sia-line); border-radius:10px;
  font-size:1rem; background:var(--sia-panel); color:var(--sia-ink); min-height:44px;
}
.sia-input:focus-visible{outline:3px solid color-mix(in srgb,var(--sia-focus) 45%,transparent); outline-offset:1px; border-color:var(--sia-accent)}
.sia-notice{
  margin:0; font-size:.86rem; line-height:1.45; color:var(--sia-attention);
  padding:10px 12px; border-radius:10px; background:color-mix(in srgb,var(--sia-attention) 10%,transparent);
}
.sia-foot{display:flex; align-items:center; gap:8px; font-size:.8rem; color:var(--sia-muted); padding-top:6px}
.sia-spinner{display:flex; gap:6px; padding:4px 0}
.sia-spinner span{width:9px; height:9px; border-radius:50%; background:var(--sia-accent); opacity:.35; animation:sia-bounce 1s infinite ease-in-out}
.sia-spinner span:nth-child(2){animation-delay:.15s} .sia-spinner span:nth-child(3){animation-delay:.3s}
@keyframes sia-bounce{0%,80%,100%{opacity:.3; transform:translateY(0)}40%{opacity:1; transform:translateY(-5px)}}
@media (prefers-color-scheme:dark){
  .sia{
    --sia-bg:#0b0d17; --sia-panel:#141826; --sia-ink:#eef1f8; --sia-muted:#9aa4bd;
    --sia-line:#252b3d; --sia-accent:#8b93f4; --sia-accent-ink:#0b0d17;
    --sia-ready:#54c493; --sia-attention:#e0b054; --sia-blocked:#f08a7a; --sia-neutral:#9aa4bd;
    --sia-focus:#8b93f4;
  }
}
:root[data-theme="dark"] .sia{
  --sia-bg:#0b0d17; --sia-panel:#141826; --sia-ink:#eef1f8; --sia-muted:#9aa4bd;
  --sia-line:#252b3d; --sia-accent:#8b93f4; --sia-accent-ink:#0b0d17;
  --sia-ready:#54c493; --sia-attention:#e0b054; --sia-blocked:#f08a7a; --sia-neutral:#9aa4bd;
  --sia-focus:#8b93f4;
}
:root[data-theme="light"] .sia{
  --sia-bg:#eef1f8; --sia-panel:#ffffff; --sia-ink:#161a26; --sia-muted:#5b6478;
  --sia-line:#e2e6f0; --sia-accent:#4f5bd5; --sia-accent-ink:#ffffff;
  --sia-ready:#1f8a5b; --sia-attention:#b7791f; --sia-blocked:#c0392b; --sia-neutral:#5b6478;
  --sia-focus:#4f5bd5;
}
@media (prefers-reduced-motion:reduce){
  .sia-spinner span{animation:none; opacity:.6}
  .sia-btn{transition:none}
}
`